import { render } from 'preact-render-to-string';
import { BasicEmailWrapper, Heading, MarketingFooter } from './shared';
import { SECTION_LABELS, Section } from 'shared/page-classification';

type SectionStat = {
  section: Section;
  uniqueChampions: number;
  totalVisits: number;
  engagedNext?: { count: number; total: number; label: string };
};

type ChampionRow = {
  userId: string;
  name: string;
  email: string;
  totalVisits: number;
  topSection: Section | string;
  topLessonTitles?: string[];
  isAtRisk: boolean;
};

type DigestData = {
  weekStart: string;
  weekLabel: string;
  totalChampions: number;
  atRiskCount: number;
  feedToThread: { visited: number; engaged: number };
  sectionStats: SectionStat[];
  championRows: ChampionRow[];
  dashboardUrl: string;
};

export function ChampionActivityDigestEmail(data: DigestData) {
  const browsedDidntEngage = Math.max(data.feedToThread.visited - data.feedToThread.engaged, 0);
  const sectionsToShow = data.sectionStats.slice(0, 8);
  const championsToShow = data.championRows.slice(0, 25);

  return render(
    <BasicEmailWrapper footer={<MarketingFooter />}>
      <Heading>Champion Activity — {data.weekLabel}</Heading>

      <p style="margin: 0 0 16px;">
        {data.totalChampions === 0
          ? 'No Champions visited the site this week.'
          : `${data.totalChampions} Champion${data.totalChampions === 1 ? '' : 's'} visited this week.`}
      </p>

      {data.totalChampions > 0 && (
        <table
          cellPadding="0"
          cellSpacing="0"
          style="border:0;border-spacing:0;width:100%;margin-bottom:24px;"
        >
          <tbody>
            <tr>
              <td style="width:33%;padding:12px;background:#f8fafc;border-radius:6px;">
                <div style="font-size:12px;color:#64748b;">Champions</div>
                <div style="font-size:24px;font-weight:600;color:#0f172a;">
                  {data.totalChampions}
                </div>
              </td>
              <td style="width:8px;"></td>
              <td style="width:33%;padding:12px;background:#fef3c7;border-radius:6px;">
                <div style="font-size:12px;color:#92400e;">At-risk</div>
                <div style="font-size:24px;font-weight:600;color:#92400e;">{data.atRiskCount}</div>
                <div style="font-size:11px;color:#92400e;">didn't click past the feed</div>
              </td>
              <td style="width:8px;"></td>
              <td style="width:33%;padding:12px;background:#f8fafc;border-radius:6px;">
                <div style="font-size:12px;color:#64748b;">Browsed feed, didn't engage</div>
                <div style="font-size:24px;font-weight:600;color:#0f172a;">
                  {browsedDidntEngage}
                </div>
                <div style="font-size:11px;color:#64748b;">
                  {data.feedToThread.engaged} of {data.feedToThread.visited} opened a thread
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {sectionsToShow.length > 0 && (
        <>
          <h3 style="margin:24px 0 12px;font-size:18px;">Where Champions went</h3>
          <table
            cellPadding="0"
            cellSpacing="0"
            style="border:0;border-spacing:0;width:100%;margin-bottom:24px;"
          >
            <thead>
              <tr style="text-align:left;color:#64748b;font-size:12px;">
                <td style="padding:6px 8px;">Section</td>
                <td style="padding:6px 8px;text-align:right;">Champions</td>
                <td style="padding:6px 8px;text-align:right;">Visits</td>
                <td style="padding:6px 8px;">Engaged → next</td>
              </tr>
            </thead>
            <tbody>
              {sectionsToShow.map((s) => (
                <tr key={s.section} style="border-top:1px solid #e2e8f0;">
                  <td style="padding:8px;font-weight:500;">
                    {SECTION_LABELS[s.section] || s.section}
                  </td>
                  <td style="padding:8px;text-align:right;">{s.uniqueChampions}</td>
                  <td style="padding:8px;text-align:right;">{s.totalVisits}</td>
                  <td style="padding:8px;color:#64748b;font-size:13px;">
                    {s.engagedNext
                      ? `${s.engagedNext.count}/${s.engagedNext.total} ${s.engagedNext.label}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {championsToShow.length > 0 && (
        <>
          <h3 style="margin:24px 0 12px;font-size:18px;">Top Champions this week</h3>
          <table
            cellPadding="0"
            cellSpacing="0"
            style="border:0;border-spacing:0;width:100%;margin-bottom:24px;"
          >
            <thead>
              <tr style="text-align:left;color:#64748b;font-size:12px;">
                <td style="padding:6px 8px;">Name</td>
                <td style="padding:6px 8px;">Top section</td>
                <td style="padding:6px 8px;text-align:right;">Visits</td>
                <td style="padding:6px 8px;">Notes</td>
              </tr>
            </thead>
            <tbody>
              {championsToShow.map((r) => (
                <tr key={r.userId} style="border-top:1px solid #e2e8f0;">
                  <td style="padding:8px;">
                    <div style="font-weight:500;">{r.name}</div>
                    <div style="color:#64748b;font-size:12px;">{r.email}</div>
                  </td>
                  <td style="padding:8px;">
                    {SECTION_LABELS[r.topSection as Section] || r.topSection}
                    {r.topLessonTitles && r.topLessonTitles.length > 0 && (
                      <div style="color:#64748b;font-size:12px;">{r.topLessonTitles[0]}</div>
                    )}
                  </td>
                  <td style="padding:8px;text-align:right;">{r.totalVisits}</td>
                  <td style="padding:8px;font-size:12px;">
                    {r.isAtRisk ? (
                      <span style="background:#fef3c7;color:#92400e;padding:2px 6px;border-radius:4px;">
                        At-risk
                      </span>
                    ) : (
                      <span style="color:#cbd5e1;">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p style="margin:24px 0 8px;">
        <a href={data.dashboardUrl} style="color:#003DA6;font-weight:500;">
          Open the full dashboard →
        </a>
      </p>

      <p style="margin:8px 0 0;color:#64748b;font-size:13px;">
        Page-by-page tracking captures every authenticated visit. Numbers update live; this digest
        is a snapshot for {data.weekLabel}.
      </p>
    </BasicEmailWrapper>,
  );
}
